import moment from 'moment-timezone';
import type React from 'react';
import {
	BlandBusyIcon,
	BlandFailedIcon,
	BlandOutgoingIcon,
	BlandSuccessIcon,
} from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/icons';
import { getCallDuration } from 'src/components/job-slide-out/components/nav-table/tenant-lula-chat/utils';
import type { TenantContactMessage } from 'src/types';

interface BlandCallMessageProps {
	message: TenantContactMessage;
	jobTimezone: string;
}

const STATUS_MAP: Record<
	NonNullable<TenantContactMessage['bland_call_status']>,
	{ Icon: React.FC; label: string }
> = {
	in_progress: { Icon: BlandOutgoingIcon, label: 'Outgoing Call' },
	completed: { Icon: BlandSuccessIcon, label: 'Call Successful' },
	failed: { Icon: BlandFailedIcon, label: 'Call Failed' },
	no_answer: { Icon: BlandBusyIcon, label: 'Call Busy' },
};

const BlandCallMessage: React.FC<BlandCallMessageProps> = ({
	message,
	jobTimezone,
}) => {
	const status = message.bland_call_status;
	const config = status ? STATUS_MAP[status] : undefined;
	const Icon = config?.Icon;

	let calledAt = '';
	if (message.bland_call_initiated_at_utc && jobTimezone) {
		const m = moment
			.utc(message.bland_call_initiated_at_utc, 'YYYY-MM-DD HH:mm:ss')
			.tz(jobTimezone);
		const now = moment.tz(jobTimezone);
		if (m.isSame(now, 'day')) {
			calledAt = `at ${m.format('hh:mmA z')}`;
		} else if (m.isSame(now.clone().subtract(1, 'day'), 'day')) {
			calledAt = `Yesterday at ${m.format('hh:mmA z')}`;
		} else {
			calledAt = m.format('MMM D, YYYY - hh:mm A z');
		}
	}
	const dateLine = `${calledAt} ${
		message.bland_call_duration_seconds
			? `for ${getCallDuration(message.bland_call_duration_seconds)}`
			: ''
	}`.trim();

	return (
		<div className="tenant-texts__row tenant-texts__row--outbound tenant-texts__row--bland">
			<div className="tenant-texts__bland">
				{Icon ? (
					<div className="tenant-texts__bland-icon" aria-hidden="true">
						<Icon />
					</div>
				) : null}
				<div className="tenant-texts__bland-data">
					<h5 className="tenant-texts__bland-title">
						{config?.label}
						{message.job_no ? (
							<>
								{' '}
								- Job #
								<span className="tenant-texts__bland-job">
									{message.job_no}
								</span>
							</>
						) : null}
						{message.bland_pathway_name ? (
							<> - {message.bland_pathway_name}</>
						) : null}
					</h5>
					{status === 'completed' && message.answered_by ? (
						<p className="tenant-texts__bland-answered">
							Answered by: {message.answered_by}
						</p>
					) : null}
					{dateLine ? (
						<p className="tenant-texts__bland-date">{dateLine}</p>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default BlandCallMessage;
